import Vue from 'vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import { getSessionToken, refreshSession } from '../api/sessionRefresh';
import store from '../store';

const baseURL = process.env.VUE_APP_API_ENDPOINT;
axios.defaults.baseURL = baseURL;
axios.defaults.withCredentials = true
let refreshFailureHandled = false;
let clearRemoteSessionPromise = null;

function clearRemoteSession() {
  if (!clearRemoteSessionPromise) {
    clearRemoteSessionPromise = axios
      .post('/authentication/logout')
      .catch(() => undefined)
      .finally(() => {
        clearRemoteSessionPromise = null;
      });
  }
  return clearRemoteSessionPromise;
}

function isDefinitiveRefreshFailure(error) {
  return [401, 403].includes(error?.response?.status);
}

export const addToken = (token) => {
  axios.defaults.headers.common['authorization'] = 'Bearer ' + token;
}

// REQUEST INTERCEPTOR
axios.interceptors.request.use(
  (request) => {
    if (String(request.url || '').includes('/authentication/login')) {
      refreshFailureHandled = false;
    }
    const token = getSessionToken();
    if (token) {
      request.headers = request.headers || {};
      request.headers.authorization = `Bearer ${token}`;
    }
    const actorEmail = store.state.user.user && store.state.user.user.email;
    if (actorEmail) {
      request.headers = request.headers || {};
      request.headers['x-actor-email'] = actorEmail;
    }
    return request;
  },
  error => Promise.reject(error)
);

// RESPONSE INTERCEPTOR
axios.interceptors.response.use(async (response) => {
  return response;
}, async (error) => {
  const originalRequest = error?.config || {};
  const response = error?.response;
  const requestUrl = String(originalRequest.url || '');
  const isAuthFlowRequest = [
    '/authentication/login',
    '/authentication/logout',
    '/authentication/refresh-token',
  ].some((path) => requestUrl.includes(path));
  const errorMessages = ['jwt expired', 'Invalid old token', 'Sesion token expired'];
  const errorStatus = [500, 401, 403];


  // Decode response json error for arrayBuffer type requests
  if (response && originalRequest.responseType === 'arraybuffer') {
    try {
      response.data = JSON.parse(new TextDecoder().decode(response.data));
    } catch (parseError) {
      // Mantener el error original si la respuesta no es un JSON de error.
    }
  }

  const responseMessage = String(
    response?.data?.error?.message || response?.data?.message || '',
  ).toLowerCase();
  const hasExpiredAuthError =
    response?.status === 401 ||
    (errorStatus.includes(response?.status) &&
      errorMessages.some((message) =>
        responseMessage.includes(message.toLowerCase()),
      ));

  if (hasExpiredAuthError && !isAuthFlowRequest && !originalRequest._retry) {
    try {
      originalRequest._retry = true;
      const token = await refreshSession();
      refreshFailureHandled = false;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.authorization = `Bearer ${token}`;
      return axios(originalRequest);
    } catch (error) {
      // A stale tab can lose a refresh rotation race (409), and network,
      // throttling, or server errors do not prove that the session is invalid.
      // Only clear the remote and local session when the refresh cookie was
      // definitively rejected by the backend.
      if (isDefinitiveRefreshFailure(error) && !refreshFailureHandled) {
        refreshFailureHandled = true;
        await clearRemoteSession();
        await store.commit('user/logout');
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
  }

  if (
    response?.data?.error?.status === 500 &&
    response?.data?.error?.message?.includes('duplicate key error')
  ) {
    await store.commit('notification/show', {
      text: "No es posible registrar a este usuario con el correo electrónico proporcionado. Si necesitas darlo de alta en esta organización, contacta al equipo de soporte",
      color: "error"
    });
  }
  return Promise.reject(error);
});

Vue.use(VueAxios, axios);
